import {_, React, observer, computed, action, classNames, useEffect, IDisposer, autorun, stringify} from '@api'
import {AssortmentCardStore, AssortmentHeroCardStore, DEBUG_ASSORTMENTS, UNASSIGNED, useAssortmentCardStore, VipLocalStore} from '@modules'
import {AssortmentHeroCard} from '@modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/Assortments/AssortmentCard/HeroCards/AssortmentHeroCard'
import {DEFAULT_ASSORTMENT_CARD_SETTINGS, StoresInstance} from '@stores'
import {observable, runInAction, toJS, trace} from 'mobx'
import FlipMove from 'react-flip-move'
import * as css from './AssortmentHeroCards.css'

export const HIDDEN_HEROES_KEY = 'HIDDEN_HEROES_KEY'
export type CombinedHeroCardEntry = AssortmentHeroCardAggData & { orientation?: 'horizontal' | 'vertical' }
export type HeroCardToRender = AssortmentHeroCardAggData & { combined?: Array<CombinedHeroCardEntry> }

export type CombinedAssortmentHeroesDescriptor = { keys: Array<string>, orientation: 'horizontal' | 'vertical' }
export type IHeroCardsOverrides = {
  orders: { [key: string]: {index: number, enabled?: boolean } }
  fullWidth: { [key: string]: boolean }
  subdivide: { [key: string]: boolean }
  combinedShapes: Array<CombinedAssortmentHeroesDescriptor>
}

export type AssortmentHeroCardAggData = {
  key: string | number | boolean
  items: Array<{ _id: string } & any>
}

/// region Store

export class AssortmentHeroCardsStore extends VipLocalStore {
  _previewReorderCardsTimeout: number

  constructor(stores: StoresInstance, public card: AssortmentCardStore) {
    super(stores)

    if (!this.overrides) {
      _.merge(this.card.assortmentCardSettings, {heroCards: {overrides: {orders: {}, combinedShapes: [], subdivide: {}, fullWidth: {}}}})
    }
  }

  @observable previewReorderCardIndex: number

  cardStores: { [index: number]: AssortmentHeroCardStore } = {}

  get dataMap() { return this.card.root.heroCardsDataMap}

  get rawData() {
    return this.card.bucket?.hero?.hero ?? {buckets: []}
  }

  get config() {
    return this.card.assortment?.heroCards
  }

  get workspace() { return this.catalog.workspace}

  @action saveOverridesToServer = async () => {
    const {overrides, catalog: {assortments: {saveOverridesForAssortment}}, card: {overridesId}, workspace} = this

    this.card.globalOverrides = _.cloneDeep(overrides)
    await saveOverridesForAssortment({assortmentKey: overridesId, workspace: workspace.id, overridesJson: stringify(overrides)})
  }

  @computed get esField() {
    return this.catalog.findFieldInTree(this.config.field, true)
  }

  @computed get cardData(): AssortmentHeroCardAggData[] {
    const {es, rawData} = this
    if (!rawData) return []

    const {buckets} = rawData
    return buckets.map(({doc_count: count, key, items}) => {
      const hits = es.unwrapHitsResult(items as any)

      return {key, items: hits.items}
    })
  }

  @action insertCustomSortedHeroAtIndex = (key, toIndex) => {
    const {overrides: {orders}, cardsInCustomOrder} = this
    orders[key] = { index: toIndex }

    DEBUG_ASSORTMENTS && console.log(`insertCustomSortedHeroAtIndex`, toJS({key, toIndex, orders}))
    let keyWasToOurRight : boolean
    for (var i = toIndex; i < cardsInCustomOrder.length; i++) {
      const k = cardsInCustomOrder[i].key?.toString() ?? UNASSIGNED

      if (k != key) {
        orders[k] = {index: i + (keyWasToOurRight ? 0 : 1)}
      }
      else {
        keyWasToOurRight = true
      }
    }
  }

  get overrides(): IHeroCardsOverrides {
    return this.card?.assortmentCardSettings?.heroCards?.overrides
  }

  @computed get hasOverrides() {
    if (!this.overrides) return false

    const {overrides: {orders, combinedShapes}} = this

    return !_.isEmpty(orders) || !_.isEmpty(combinedShapes)
  }

  @computed get hasCombines() {
    if (!this.overrides) return false
    const {overrides: {combinedShapes}} = this

    return !_.isEmpty(combinedShapes)
  }

  @computed get hasSortOverrides() {
    if (!this.overrides) return false
    const {overrides: {orders}} = this

    return !_.isEmpty(orders)
  }

  @action clearOverrides = () => {
    _.assign(this.card.assortmentCardSettings.heroCards, {overrides: _.cloneDeep(DEFAULT_ASSORTMENT_CARD_SETTINGS.heroCards.overrides)})
  }

  @action clearSortOverrides = () => {
    this.card.assortmentCardSettings.heroCards.overrides.orders = {}
  }

  @action clearCombinesOverrides = () => {
    this.card.assortmentCardSettings.heroCards.overrides.combinedShapes = []
  }

  @observable.ref heroCardsRef: HTMLDivElement
  @action onSetHeroCardsRef = ref => this.heroCardsRef = ref

  hiddenHeroes = observable.array<any>()

  @action showHiddenHeroes = () => {
    this.card.toggleShowAllHeroes()
  }

  @computed get keysToOriginalSortIndex() {
    return this.cardsInOriginalOrderAndCombined.reduce((acc, v, i) => ({...acc, [v.key?.toString() ?? UNASSIGNED]: i}), {})
  }

  @computed get sortOverridesKeyToSortIndex() {
    const {overrides} = this
    return new Map<number, string>(_.entries(overrides?.orders ?? {}).filter(([k,v]) => v.enabled !== false).map(([key, {index}]) => [index, key]))
  }

  @computed get enabledOrderOverrideKeys() {
    const {overrides, keysToOriginalSortIndex} = this
    return _.keys(overrides?.orders).filter(k => keysToOriginalSortIndex[k] && overrides.orders[k].enabled !== false)
  }

  @computed get cardsInCustomOrder(): Array<HeroCardToRender>{
    const {overrides, enabledOrderOverrideKeys, cardsInOriginalOrderAndCombined: heroCards} = this
    if (_.isEmpty(enabledOrderOverrideKeys)) return heroCards

    const {orders} = overrides, {keysToOriginalSortIndex: keysToOriginalIndex, sortOverridesKeyToSortIndex: heroBySortIndex} = this
    let r = [...heroCards]

    // Need to move the items from left to right of their locations to the ones specified in the custom sort order list
    // Items with overrides may not be present since they could currently be combined with someone else
    const keysInInitialOrder = _.orderBy(enabledOrderOverrideKeys, k => keysToOriginalIndex[k]),
      keysInFinalOrder = _.orderBy(enabledOrderOverrideKeys, k => orders[k].index)

    // DEBUG_ASSORTMENTS && console.debug(`Custom sort heroes`, toJS({keysInFinalOrder, keysInInitialOrder, keysToOriginalIndex, orders: overrides.orders, heroBySortIndex, initial: heroCards}, {recurseEverything: true}))

    // Remove all the items to move from the current array
    const itemsToMove = keysInInitialOrder.reduce((acc, k) => {
      const index = keysToOriginalIndex[k]
      return {...acc, [k]: r[index]}
    }, {})

    keysInInitialOrder.forEach((key, i) => {
      const initialIndex = keysToOriginalIndex[key],
        currentIndex = initialIndex - i

      // DEBUG_ASSORTMENTS && console.log(`Remove element ${key} from initial array at position ${initialIndex - i}`, {
      //   r, initialIndex, offset: i, currentIndex,
      //   expected: itemsToMove[i], actual: r[currentIndex]})

      const deleted = r.splice(initialIndex - i, 1)
      if (deleted[0] != itemsToMove[key]) debugger
    })

    keysInFinalOrder.forEach((key, i) => {
      const item = itemsToMove[key],
        initialIndex = keysToOriginalIndex[key],
        sortIndex = orders[key].index

     // DEBUG_ASSORTMENTS && console.log(`Insert '${key}' at index ${sortIndex} (original ${initialIndex})`, {before: r.slice()})

      r.splice(sortIndex, 0, item)

      // DEBUG_ASSORTMENTS && console.log(`After:`, r.slice())
    })

    return r
  }


  @computed get cardsInOriginalOrderAndCombined() : Array<HeroCardToRender> {
    const {cardData, overrides, config} = this
    let r: Array<HeroCardToRender> = _.cloneDeep(cardData)

    if (!_.isEmpty(overrides?.combinedShapes)) {
      // console.log(r)
      const lookups = _.keyBy(r, r => r.key.toString())

      overrides.combinedShapes.forEach(({keys, orientation}) => {
        const original = lookups[keys[0].toString()]

        if (original) {
          const toInsertIndex = r.indexOf(original)

          if (toInsertIndex != -1) {
            const combinedHeroCard: HeroCardToRender = _.cloneDeep(original)
            r[toInsertIndex] = combinedHeroCard

            for (let i = 1; i < keys.length; i++) {
              const item = lookups[keys[i]]
              combinedHeroCard.combined = [...(combinedHeroCard.combined ?? []), {key: keys[i], orientation, ...item}]
              item && _.remove(r, item)
            }
          }
        }
      })
    }

    if (config?.orderBy) {
      const {orderBy: {field, order}} = config
      // console.log(`Before`, r)
      r = _.orderBy(r, v => _.get(_.first(v.items), field), order ?? 'asc')
      // console.log(`After`, r)
    }

    return r
  }

  @computed get cardsToRender() {
    const {cardsInCustomOrder, card: {showAllHeroes}, catalog, catalog: {settings: {viewMode, assortment: {layout: {heroesPerAssortment}, hero: {hideEmptyRows}}}}} = this

    let r = [...cardsInCustomOrder]

    // const {previewReorderCardIndex} = this
    // if (_.isNumber(previewReorderCardIndex)) {
    //   const {dragDrop: {dragItem}} = this
    //
    //   // trace()
    //   if (dragItem?.dragType === 'assortmentHeroCard') {
    //     const {targetExtra} = dragItem,
    //       originalIndex: number = targetExtra?.originalIndex,
    //       item = r[originalIndex]
    //
    //     // console.log(previewReorderCardIndex)
    //     if (originalIndex !== previewReorderCardIndex) {
    //       r.splice(originalIndex, 1)
    //       r.splice(Math.max(0, previewReorderCardIndex >= originalIndex ? previewReorderCardIndex - 1 : previewReorderCardIndex), 0, item)
    //     }
    //   }
    // }

    const hiddenHeroes = showAllHeroes ? [] : r.slice(heroesPerAssortment)

    r = showAllHeroes ? r : _.take(r, heroesPerAssortment) // + ((r.length - 1) % this.card.assortmentSettings.layout.columns)) // Don't throw away cards if we have room to show them in the multi-column layout

    const {card: {assortmentSettings: {layout: {columns}, hero: {fullWidthRows}}}} = this

    /* Fill in extra item(s) so that we don't have empty holes in the assortment row

    This is complicated.  Consider:

                          X
          X               X             X
          X               X             X
       ...and 2 more

     cards: 9, columns 3, fullWidthRows: 1, maxCards: 7

     Should instead render EITHER:
                          X
          X               X             X
          X               X             X
          X               X             P

     or (what we do below):
                          X
          X               X             X
          X               X             ...and 3 more

     */

    let cardsInLastRow = (r.length - fullWidthRows.length) % columns,
      fillerCellCount = cardsInLastRow === 0 ? 0 : columns - cardsInLastRow

    // console.log({cardsOrig: r.length, r, hiddenHeroes, cardsNoFullWidth: r.length - fullWidthRows.length, columns, fillerCellCount})

    if (!_.isEmpty(hiddenHeroes) && viewMode != 'pdf') {
      if (cardsInLastRow === 0) {
        // Remove the last real item that was rendered and move it into the '...and N more' hero

        hiddenHeroes.splice(0, 0, _.last(r))
        r.splice(r.length - 1, 1)
        r.push({key: HIDDEN_HEROES_KEY, items: []})
      } else {
        /** Something like:
         *             X               X             X
         *             X               P             P
         *  After (depends on how many heroes we have remaining to show:
         X               X             X             X
         X               X             X        ...and # more
         */

        while ((fillerCellCount > 1 && hiddenHeroes.length > 0) || (fillerCellCount === 1 && hiddenHeroes.length === 1)) {
          const hero = _.first(hiddenHeroes)
          hiddenHeroes.splice(0, 1)
          r.push(hero)
          fillerCellCount--
        }

        if (!_.isEmpty(hiddenHeroes)) {
          r.push({key: HIDDEN_HEROES_KEY, items: []})
          fillerCellCount--
        }
      }
    }

    fillerCellCount > 0 && r.push(...new Array(fillerCellCount))

    if (r.length < heroesPerAssortment && !hideEmptyRows) {
      r.push(...new Array(heroesPerAssortment - r.length))
    }

    const holes = (r.length - fullWidthRows.length) % columns
    if (holes > 0) {
      r.push(...new Array(holes))
    }

    setTimeout(() => {
      this.hiddenHeroes.replace(hiddenHeroes)
    }, 50)

    return r
  }

  @action onContextMenu = (e: React.MouseEvent) => {
    const {contextMenu} = this
    contextMenu.onComponentContextMenu(e, this)
  }

  get root() { return this.card.root}

  get hoverItem() { return this.card.hoverItem }

  mouseEventsFor: (hero: AssortmentHeroCardStore) => Partial<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>> = _.memoize(hero => ({
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation() // Otherwise click on group will trigger and clear our selection
      // this.root.selectContextualHoverCatalogItems(e)
    }
  }))

  dragPlaceholderFor = _.memoize((hero: AssortmentHeroCardStore) => {
    const {esField, dragDrop, catalog} = this
    return dragDrop.handlersFor({target: esField.esIndex, id: hero.id})
  })

  get catalogFieldSkeleton() { return this.root.hero_catalogFieldSkeleton }

  get foreignKeyFieldSkeleton() {return this.root.hero_foreignKeyFieldSkeleton }

  get foreignKeyFieldRealized() {return this.root.hero_foreignKeyFieldRealized }

  get assortmentSettings() { return this.root.settings}
  registerEffects = () => {
    _.merge(this.overrides, DEFAULT_ASSORTMENT_CARD_SETTINGS.heroCards.overrides)

    const _toDispose: Array<IDisposer> = [
      autorun(() => {
        const {overrides, cardsToRender, cardData} = this
        DEBUG_ASSORTMENTS && console.log(toJS({overrides, cardsToRender, cardData}, {recurseEverything: true}))
      }),
      autorun(() => {
        const {cardsToRender} = this
        runInAction(() => {
          const {assortmentSettings: {byCard}, cardsToRender, card: {id}} = this,
            defaultChildSettings = {[id]: {heroCards: {secondLevel: {selections: cardsToRender.reduce((acc, v) => ({...acc, [v?.key?.toString?.() ?? UNASSIGNED]: {selected: 0}}), {})}}}}
          _.merge(byCard, defaultChildSettings)
        })
      }),
      autorun(() => {
        const {previewReorderCardIndex, dragDrop} = this
        if (previewReorderCardIndex) {
          if (!dragDrop.isDragging) {
            this.previewReorderCardIndex = null
          } else {
            // console.log(`previewReorderCard:`, this.previewReorderCardIndex)
          }
        }
      })
    ]

    return () => {
      _toDispose.forEach(d => d())
    }
  }
}

export const AssortmentHeroCardsStoreContext = React.createContext<AssortmentHeroCardsStore>(null)
export const useAssortmentHeroCardsStore = () => React.useContext(AssortmentHeroCardsStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
}

export const AssortmentHeroCards = observer<MyProps, HTMLDivElement>(function AssortmentHeroCards({className, ...props}, ref) {
  const store = useAssortmentCardStore(),
    {componentStores: {heroCards, heroCards: {cardsToRender, onSetHeroCardsRef}}, root: {animations, settings: {columnView}}} = store

  useEffect(() => heroCards.registerEffects(), [])

  // DEBUG_ASSORTMENTS && console.log(`render heroes`, toJS(cardsToRender))

  return (
    <AssortmentHeroCardsStoreContext.Provider value={heroCards}>
      <div className={classNames(css.root, className)}
           ref={ref}
           data-contextual-hover-item={undefined}
        // onContextMenu={onContextMenu}
      >
        <FlipMove
          disableAllAnimations={columnView}
          ref={onSetHeroCardsRef}
          className={css.container}
          {...animations.heroCards}
          //onMouseLeave={onMouseLeave}
          //style={{'--object-fit': catalog.objectFitFor(associatedWorkspace)} as any}
        >
          {cardsToRender.map((card, i) =>
            <AssortmentHeroCard key={card?.key?.toString() ?? i} data={card} className={classNames(css.heroCard)} index={i}/>
          )}
        </FlipMove>
      </div>
    </AssortmentHeroCardsStoreContext.Provider>)
}, {forwardRef: true})

AssortmentHeroCards['whyDidYouRender'] = true

/// endregion




// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/Assortments/AssortmentCard/HeroCards/AssortmentHeroCards.tsx